import React, { useRef, useState, useEffect } from 'react';
import { MdAdd, MdMoreVert, MdEdit, MdDelete } from 'react-icons/md';

const WatchListTabs = ({ groups, selectedGroupId, onSelectGroup, onCreateGroup, onRenameGroup, onDeleteGroup }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Menu State
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const scrollRef = useRef(null);

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
         setMenuOpenId(null);
      }
    };
    if (menuOpenId) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenId]);

  // Handle scroll to close menu to avoid floating issues
  useEffect(() => {
     const handleScroll = () => {
        if(menuOpenId) setMenuOpenId(null);
     };
     window.addEventListener('scroll', handleScroll, true);
     return () => window.removeEventListener('scroll', handleScroll, true);
  }, [menuOpenId]);


  const handleCreate = (e) => {
    e.preventDefault();
    if (newGroupName.trim()) {
      onCreateGroup(newGroupName.trim());
      setNewGroupName('');
      setIsCreating(false);
      setTimeout(() => {
        if(scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
      }, 100);
    }
  };

  const startEdit = (group) => {
    setEditingId(group.id);
    setEditName(group.name);
    setMenuOpenId(null);
  };

  const handleRename = (e) => {
    e.preventDefault();
    if (editName.trim()) {
      onRenameGroup(editingId, editName.trim());
      setEditingId(null);
    }
  };

  const openMenu = (e, groupId) => {
      e.stopPropagation();
      if (menuOpenId === groupId) {
          setMenuOpenId(null);
          return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
          top: rect.bottom + 8,
          left: rect.left
      });
      setMenuOpenId(groupId);
  };

  return (
    <>
      {/* Tab strip — sits inside the DS Toolbar nav slot. Self-scrolls (scrollRef
          drives scroll-to-end after create); 32px calm pills per the DS reference. */}
      <div
        ref={scrollRef}
        className="flex items-center gap-1 overflow-x-auto no-scrollbar w-full"
      >
        {groups.map((group) => {
          const isActive = selectedGroupId === group.id;
          return (
            <div
              key={group.id}
              className={`flex-shrink-0 relative group flex items-center h-8 pl-3 pr-1.5 rounded-md transition-colors duration-200 cursor-pointer select-none
                ${isActive
                  ? 'bg-bg-3 text-fg-1'
                  : 'text-fg-3 hover:text-fg-1 hover:bg-bg-3'
                }`}
              onClick={() => onSelectGroup(group.id)}
            >
              {editingId === group.id ? (
                <form onSubmit={handleRename}>
                   <input
                      autoFocus
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => setEditingId(null)}
                      className="bg-transparent border-b border-[var(--border)] focus:border-[var(--fg-1)] outline-none text-[13px] w-28 text-fg-1 font-semibold tracking-wide"
                      onClick={(e) => e.stopPropagation()}
                   />
                </form>
              ) : (
                <span className={`text-[13px] tracking-wide transition-all duration-200 ${isActive ? 'font-semibold' : 'font-medium'}`}>
                  {group.name}
                </span>
              )}

              {/* Instrument count — bubble, matches the DS tab badge. Active fill
                  uses color-mix (the accent token has no alpha channel, so
                  Tailwind's bg-accent/20 renders no background). */}
              {!editingId && (
                <span
                  className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold leading-none tabular-nums ${
                    isActive ? 'text-accent' : 'bg-bg-3 text-fg-2'
                  }`}
                  style={isActive ? { background: 'color-mix(in oklch, var(--color-accent) 22%, transparent)' } : undefined}
                >
                  {group.instruments?.length || 0}
                </span>
              )}

              <button
                className={`ml-1 -mr-0.5 p-1 rounded hover:bg-bg-2 opacity-0 group-hover:opacity-100 transition-all duration-200 ${isActive ? 'text-fg-2' : 'text-fg-3'}`}
                onClick={(e) => openMenu(e, group.id)}
              >
                <MdMoreVert className="text-base" />
              </button>
            </div>
          );
        })}

        {isCreating ? (
          <form onSubmit={handleCreate} className="flex-shrink-0 animate-in fade-in zoom-in duration-200">
            <input
              autoFocus
              type="text"
              placeholder="GROUP NAME"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              onBlur={() => !newGroupName && setIsCreating(false)}
              className="h-8 px-3 rounded-md bg-bg-2 text-[11px] font-bold tracking-widest uppercase outline-none ring-1 ring-primary/50 text-fg-1"
            />
          </form>
        ) : (
          <button
             onClick={() => setIsCreating(true)}
             title="New watchlist"
             className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-md border border-dashed border-[var(--divider)] text-fg-3 hover:text-fg-1 hover:border-[var(--fg-3)] hover:border-solid transition-colors duration-200"
          >
             <MdAdd className="text-lg" />
          </button>
        )}
      </div>

      {/* PORTAL / FIXED MENU OVERLAY */}
      {menuOpenId && (
         <div
            ref={menuRef}
            className="fixed w-40 bg-bg-2 rounded-xl shadow-2xl border border-[var(--border)] z-[9999] overflow-hidden py-2 animate-in fade-in zoom-in-95 duration-200"
            style={{
               top: menuPosition.top,
               left: menuPosition.left
            }}
         >
            <button
               onClick={(e) => {
                  e.stopPropagation();
                  const group = groups.find(g => g.id === menuOpenId);
                  if(group) startEdit(group);
               }}
               className="w-full text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-fg-2 hover:bg-bg-3 hover:text-primary transition-colors flex items-center gap-3"
            >
               <MdEdit className="text-sm" /> Rename
            </button>
            <button
               onClick={(e) => {
                  e.stopPropagation();
                  const id = menuOpenId;
                  setMenuOpenId(null);
                  // Hand off to the page's named ConfirmationModal (which shows
                  // the list name + instrument count) instead of window.confirm.
                  onDeleteGroup(id);
               }}
               className="w-full text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-fg-2 hover:bg-danger/10 hover:text-danger transition-colors flex items-center gap-3"
            >
               <MdDelete className="text-sm" /> Delete
            </button>
         </div>
      )}
    </>
  );
};

export default WatchListTabs;
